import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { combineLatest, distinctUntilChanged, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { DolbyService } from '../../services/dolby/dolby.service';
import { NameService } from '../../services/name/name.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { UserService } from '../../services/user/user.service';
import { VideoOffService } from '../../services/video-off/video-off.service';
import { MuteService } from '../../services/mute/mute.service';
import { LiveDolbyParticipant } from '@sc/types';
import { SessionsService } from '../../services/sessions/sessions.service';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { ParticipantEquipmentComponent } from '../participant-equipment/participant-equipment.component';
import { EquipmentService } from '../../services/equipment/equipment.service';
import { PopoverController } from '@ionic/angular';
import { defaultPopoverOptions } from '../../services/theme/theme.service';
import { StudioSidebarService } from '../../services/studio-sidebar/studio-sidebar.service';
import { StatsService } from '../../services/stats/stats.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { Roles } from '@sc/types';
import { RolesService } from '../../services/roles/roles.service';
import { RecordingService } from '../../services/recording/recording.service';
import { MiniNoteType, MiniNotification } from '@sc/types';

@Component({
  selector: 'sc-dolby-participant',
  templateUrl: './dolby-participant.component.html',
  styleUrls: ['./dolby-participant.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(100%)' }),
        animate('100ms', style({ opacity: 1, transform: 'translateX(0%)' })),
      ]),
      transition(':leave', [animate('100ms', style({ opacity: 0, transform: 'translateY(-100%)' }))]),
    ]),
  ],
})
export class DolbyParticipantComponent implements OnInit, OnDestroy, OnChanges {
  @Input() dolbyParticipant: LiveDolbyParticipant;

  user$ = this.userService.activeUser$;
  session$ = this.sessionsService.studioSession$;

  isLocalParticipant: boolean;
  isScreenShare = false;
  mute: boolean;
  name: string;
  videoOff: boolean;
  width: number;
  notifications = [];
  mutedNotifications = new Map<string, number>();
  MUTE_TIME = 1000 * 60 * 5; // 5 minutes
  toggleStats = this.studioSidebarService.toggleStats.bind(this.studioSidebarService);

  subs: Array<Subscription> = [];

  constructor(
    private analyticsService: AnalyticsService,
    private dolbyService: DolbyService,
    private equipmentService: EquipmentService,
    private muteService: MuteService,
    private nameService: NameService,
    private popoverController: PopoverController,
    private recordingService: RecordingService,
    private rolesService: RolesService,
    private sessionsService: SessionsService,
    private statsService: StatsService,
    private studioSidebarService: StudioSidebarService,
    private toastrService: ToastrService,
    private userService: UserService,
    private videoOffService: VideoOffService
  ) {}

  /**
   * Initializes the participant component
   */
  ngOnInit() {
    this.setupMute();
    this.setupVideoOff();
    this.setupName();
    this.watchNotifications();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.dolbyParticipant.currentValue) {
      if (changes.dolbyParticipant.currentValue.activeStream?.type === 'ScreenShare') {
        this.isScreenShare = true;
      } else this.isScreenShare = false;
      if (changes.dolbyParticipant.currentValue.info.externalId === this.user$.value.uid) {
        this.isLocalParticipant = true;
        if (!this.isScreenShare) this.manageNotifications();
      } else {
        this.isLocalParticipant = false;
      }
    }
  }

  trackByID(index, value) {
    return value.id;
  }

  watchNotifications() {
    const sub = this.sessionsService
      .getParticipantNotifications(this.session$.value.sessionID, this.dolbyParticipant.info.externalId)
      .pipe(distinctUntilChanged())
      .subscribe((notifications) => {
        if (this.isScreenShare) return;
        if (this.session$?.value?.confType === 'daily') return;

        this.notifications = notifications.filter((n) => {
          if (!this.isLocalParticipant && n.role && n.role > this.rolesService.role$.value) return false;
          if (this.mutedNotifications.has(n.id)) {
            const mutedUntil = this.mutedNotifications.get(n.id);
            if (mutedUntil > Date.now()) return false;
            else this.mutedNotifications.delete(n.id);
          }
          if (n.dismissed === undefined) return true;
        });
      });
    this.subs.push(sub);
  }

  dismissNotification(notification) {
    this.mutedNotifications.set(notification.id, Date.now() + this.MUTE_TIME);
    this.sessionsService.dismissParticipantNotification(
      notification.id,
      this.session$.value.sessionID,
      this.dolbyParticipant.info.externalId
    );
  }

  manageNotifications() {
    const sub = this.statsService.connectionStats$.subscribe((stats) => {
      const sID = this.session$.value.sessionID;
      const pID = this.dolbyParticipant.info.externalId;
      if (
        stats.scores &&
        ((stats.scores.audio && stats.scores.audio < 3) ||
          (stats.scores.video && stats.scores.video < 1) ||
          stats.network.currentRoundTripTime > 0.5)
      ) {
        const score = stats.scores.video || stats.scores.audio;
        const icon = score < 2 ? 'wifi-error.svg' : 'wifi-very-weak.svg';
        const color = score < 1 ? 'error' : 'warning';
        const label = `Network is struggling`;
        const notification: MiniNotification = {
          label,
          icon,
          color,
          role: Roles.SHOW_MANAGER,
          topic: 'networkStruggling',
        };
        console.warn(
          `Network Struggling - Ping: ${stats.network.currentRoundTripTime * 1000}ms ` +
            `AudioMOS: ${stats.scores.audio.toFixed(2)} ` +
            `VideoMOS: ${stats.scores.video.toFixed(2)}`
        );
        this.sessionsService.setParticipantNotification(MiniNoteType.NET, notification, sID, pID);
      } else if (this.notifications.find((n) => n.id === MiniNoteType.NET)) {
        this.sessionsService.dismissParticipantNotification(MiniNoteType.NET, sID, pID);
      }

      if (stats.network.qualityLimitationReason) {
        const label = 'Live quality limited: ' + stats.network.qualityLimitationReason;
        const notification: MiniNotification = {
          label,
          icon: 'wifi-error.svg',
          color: 'warning',
          role: Roles.SHOW_MANAGER,
          topic: 'networkStruggling',
        };
        this.sessionsService.setParticipantNotification(MiniNoteType.QUALITY, notification, sID, pID);
      } else if (this.notifications.find((n) => n.id === MiniNoteType.QUALITY)) {
        this.sessionsService.dismissParticipantNotification(MiniNoteType.QUALITY, sID, pID);
      }
    });
    this.subs.push(sub);

    const sub2 = combineLatest([this.recordingService.recording$, this.statsService.recordingStats$]).subscribe(
      ([isRecording, stats]) => {
        const aQPressure = stats.audio.totalChunks > 10 ? stats.audio.chunkQueueSize / stats.audio.totalChunks : 0;
        const vQPressure = stats.video.totalChunks > 10 ? stats.video.chunkQueueSize / stats.video.totalChunks : 0;
        const sQPressure = stats.screen.totalChunks > 10 ? stats.screen.chunkQueueSize / stats.screen.totalChunks : 0;
        if (isRecording && (aQPressure > 0.5 || vQPressure > 0.5 || sQPressure > 0.5)) {
          const label = 'Primary uploads are slow';
          const icon = 'upload.svg';
          const hasPressure = aQPressure > 0.8 || vQPressure > 0.8 || sQPressure > 0.8;
          const notification: MiniNotification = {
            label,
            icon,
            color: hasPressure ? 'error' : 'warning',
            role: Roles.SHOW_MANAGER,
            topic: 'networkStruggling',
          };
          this.sessionsService.setParticipantNotification(
            MiniNoteType.QUEUE,
            notification,
            this.session$.value.sessionID,
            this.dolbyParticipant.info.externalId
          );
        } else if (this.notifications.find((n) => n.id === MiniNoteType.QUEUE)) {
          this.sessionsService.dismissParticipantNotification(
            MiniNoteType.QUEUE,
            this.session$.value.sessionID,
            this.dolbyParticipant.info.externalId
          );
        }
      }
    );
    this.subs.push(sub2);

    const sub3 = combineLatest([this.recordingService.recording$, this.userService.appFocused$]).subscribe(
      ([isRecording, isFocused]) => {
        if (isRecording && !isFocused) {
          const label = 'App is not focused';
          const icon = 'focus-tab.svg';
          const color = 'warning';
          const notification: MiniNotification = { label, icon, color, role: Roles.SHOW_MANAGER, topic: 'appFocus' };
          this.sessionsService.setParticipantNotification(
            MiniNoteType.FOCUS,
            notification,
            this.session$.value.sessionID,
            this.dolbyParticipant.info.externalId
          );
        } else if (this.notifications.find((n) => n.id === MiniNoteType.FOCUS)) {
          this.sessionsService.dismissParticipantNotification(
            MiniNoteType.FOCUS,
            this.session$.value.sessionID,
            this.dolbyParticipant.info.externalId
          );
        }
      }
    );
    this.subs.push(sub3);
  }

  setupMute() {
    const sub = this.muteService
      .getMute(this.session$.value.sessionID, this.dolbyParticipant.info.externalId)
      .subscribe((mute) => {
        this.mute = mute;
      });
    this.subs.push(sub);
  }
  setupVideoOff() {
    if (this.isScreenShare) {
      this.videoOff = false;
      return;
    }
    const sub = this.videoOffService
      .getVideoOff(this.session$.value.sessionID, this.dolbyParticipant.info.externalId)
      .subscribe((videoOff) => {
        this.videoOff = videoOff;
      });

    this.subs.push(sub);
  }
  setupName() {
    const sub = this.nameService
      .getName(this.session$.value.sessionID, this.dolbyParticipant.info.externalId)
      .subscribe((name) => {
        this.name = name;
      });

    this.subs.push(sub);
  }

  widthChanged(width: number) {
    if (!width) return;

    this.width = width;
  }

  async openEquipmentPopover(click: MouseEvent) {
    const equipment$ = this.equipmentService.getParticipantEquipment(
      this.dolbyParticipant.info.externalId,
      this.session$.value.sessionID
    );
    const menu = await this.popoverController.create({
      ...defaultPopoverOptions,
      side: 'top',
      size: 'auto',
      showBackdrop: false,
      component: ParticipantEquipmentComponent,
      componentProps: { expanded: true, equipment$, uid: this.dolbyParticipant.info.externalId },
      cssClass: 'participant-equipment-popover',
      event: click,
    });
    await menu.present();
    await this.analyticsService.track('clicked on participant name');
  }

  /**
   * Minimizes the local participant
   */
  minimizeParticipant() {
    this.dolbyService.minimizeLocalParticipant(true);
    this.toastrService.success('You hid your own portrait (Others can still see you)', 'Successfully Hid Yourself', {
      progressBar: true,
      progressAnimation: 'decreasing',
      closeButton: true,
      tapToDismiss: false,
      timeOut: 5 * 1000,

      toastComponent: GeneralToastComponent,
    });
    this.analyticsService.track('minimized local participant', { minimized: true });
  }

  pinParticipant() {
    if (this.isScreenShare)
      this.dolbyService.setPrioritySpeaker(
        this.dolbyParticipant.info.externalId + '_screen',
        !this.dolbyParticipant.prioritySpeaker
      );
    else {
      this.dolbyService.setPrioritySpeaker(
        this.dolbyParticipant.info.externalId,
        !this.dolbyParticipant.prioritySpeaker
      );
    }

    this.analyticsService.track('pinned participant', { participantUID: this.dolbyParticipant.info.externalId });
  }

  toggleObjectFitCover() {
    if (this.isScreenShare)
      this.dolbyService.setObjectFitCover(
        this.dolbyParticipant.info.externalId + '_screen',
        this.dolbyParticipant.objectFit === 'cover' ? 'contain' : 'cover'
      );
    else {
      this.dolbyService.setObjectFitCover(
        this.dolbyParticipant.info.externalId,
        this.dolbyParticipant.objectFit === 'contain' ? 'cover' : 'contain'
      );
    }

    this.analyticsService.track('toggled participant object fit', { objectFit: this.dolbyParticipant.objectFit });
  }

  /**
   * Unsubscribes from all observables
   */
  ngOnDestroy() {
    this.subs.map((sub: Subscription) => {
      sub.unsubscribe();
    });
  }
}
